import React, { useCallback } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import MachineMultiSelect from './MachineMultiSelect';
import CardDigitsInput from './CardDigitsInput';
import ProductMultiSelect from './ProductMultiSelect';

export interface OrderFiltersType {
  machineUuids: string[];
  last4Digits: string;
  productUuids: string[];
}

interface OrderFiltersProps {
  filters: OrderFiltersType;
  onFiltersChange: (filters: OrderFiltersType) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  isLoading?: boolean;
}

const OrderFilters: React.FC<OrderFiltersProps> = React.memo(({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  isLoading = false
}) => {
  const handleMachineChange = useCallback((machineUuids: string[]) => {
    onFiltersChange({ ...filters, machineUuids });
  }, [filters, onFiltersChange]);

  const handleCardDigitsChange = useCallback((last4Digits: string) => {
    onFiltersChange({ ...filters, last4Digits });
  }, [filters, onFiltersChange]);

  const handleProductChange = useCallback((productUuids: string[]) => {
    onFiltersChange({ ...filters, productUuids });
  }, [filters, onFiltersChange]);

  const hasActiveFilters = 
    filters.machineUuids.length > 0 || 
    filters.last4Digits.length > 0 || 
    filters.productUuids.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Order Filters</h3>
        <p className="text-sm text-gray-600">Filter orders by machine, payment card, or products</p>
      </div>
      
      <Row className="g-3 mb-4">
        <Col md={4}>
          <MachineMultiSelect
            selectedMachines={filters.machineUuids}
            onSelectionChange={handleMachineChange}
            disabled={isLoading}
          />
        </Col>
        <Col md={4}>
          <CardDigitsInput
            value={filters.last4Digits}
            onChange={handleCardDigitsChange}
            disabled={isLoading}
          />
        </Col>
        <Col md={4}>
          <ProductMultiSelect
            selectedProducts={filters.productUuids}
            onSelectionChange={handleProductChange}
            disabled={isLoading}
          />
        </Col>
      </Row>
      
      <div className="d-flex gap-2">
        <Button 
          variant="primary" 
          onClick={onApplyFilters}
          disabled={isLoading}
        >
          Apply Filters
        </Button>
        <Button 
          variant="outline-secondary" 
          onClick={onClearFilters}
          disabled={isLoading || !hasActiveFilters}
        >
          Clear Filters
        </Button>
        {hasActiveFilters && (
          <span className="d-flex align-items-center text-sm text-gray-600 ms-2">
            {[
              filters.machineUuids.length > 0 && `${filters.machineUuids.length} machine${filters.machineUuids.length === 1 ? '' : 's'}`,
              filters.last4Digits && 'card digits',
              filters.productUuids.length > 0 && `${filters.productUuids.length} product${filters.productUuids.length === 1 ? '' : 's'}`
            ].filter(Boolean).join(', ')} selected
          </span>
        )}
      </div>
    </div>
  );
});

OrderFilters.displayName = 'OrderFilters';

export default OrderFilters;